import { cleanupCmtsAppOwnedCaches } from "../modules/core/pwa/cmtsCacheCleanup.js";
import { cmtsPwaRuntimeVersion } from "../modules/core/pwa/cmtsPwaVersion.js";

export type CmtsServiceWorkerRegistrationOptions = {
  enabled?: boolean | undefined;
  serviceWorkerUrl?: string | undefined;
  onReady?: ((registration: ServiceWorkerRegistration) => void) | undefined;
  onUpdate?: ((registration: ServiceWorkerRegistration) => void) | undefined;
  onError?: ((error: unknown) => void) | undefined;
};

let controllerChangeListenerInstalled = false;
let registrationLoadListenerInstalled = false;
let awaitingUserAcceptedUpdate = false;

function dispatchPwaEvent(type: string, detail: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(type, { detail }));
}

function reportServiceWorkerError(options: CmtsServiceWorkerRegistrationOptions, error: unknown, operation = "service-worker"): void {
  options.onError?.(error);
  dispatchPwaEvent("cmts:pwa-error", { error, operation });
}

function reportServiceWorkerUpdate(registration: ServiceWorkerRegistration, options: CmtsServiceWorkerRegistrationOptions): void {
  options.onUpdate?.(registration);
  dispatchPwaEvent("cmts:pwa-update", {
    scope: registration.scope,
    serviceWorkerVersion: cmtsPwaRuntimeVersion.serviceWorkerVersion,
    appVersion: cmtsPwaRuntimeVersion.appVersion,
  });
}

function postToWorker(worker: ServiceWorker | null | undefined, message: Record<string, unknown>): boolean {
  try {
    if (!worker) return false;
    worker.postMessage(message);
    return true;
  } catch (error: unknown) {
    dispatchPwaEvent("cmts:pwa-error", { error, operation: "postMessage" });
    return false;
  }
}

function installControllerChangeReload(): void {
  if (controllerChangeListenerInstalled || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  controllerChangeListenerInstalled = true;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading || !awaitingUserAcceptedUpdate) return;
    reloading = true;
    window.location.reload();
  });
}

function installServiceWorkerMessageBridge(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data as { type?: string; payload?: Record<string, unknown> } | undefined;
    if (!data?.type) return;
    if (data.type === "CMTS_CACHE_CLEANUP_COMPLETED") {
      dispatchPwaEvent("cmts:pwa-cache-cleanup-completed", data.payload ?? {});
    }
    if (data.type === "CMTS_PWA_UPDATE_FOUND" || data.type === "CMTS_PWA_UPDATE_READY") {
      dispatchPwaEvent("cmts:pwa-update", data.payload ?? {});
    }
    if (data.type === "CMTS_PWA_ERROR") {
      dispatchPwaEvent("cmts:pwa-error", data.payload ?? {});
    }
  });
}

export async function applyCmtsPwaUpdate(): Promise<boolean> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  const worker = registration?.waiting ?? registration?.installing;
  if (!worker) return false;
  awaitingUserAcceptedUpdate = true;
  return postToWorker(worker, { type: "CMTS_SKIP_WAITING", serviceWorkerVersion: cmtsPwaRuntimeVersion.serviceWorkerVersion });
}

export async function runCmtsPwaCacheCleanup(): Promise<void> {
  const result = await cleanupCmtsAppOwnedCaches();
  dispatchPwaEvent("cmts:pwa-cache-cleanup-completed", result);
  const registration = typeof navigator !== "undefined" && "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
  postToWorker(registration?.active, { type: "CMTS_CLEAR_STALE_CACHES" });
}

export function registerCmtsServiceWorker(options: CmtsServiceWorkerRegistrationOptions = {}): void {
  const enabled = options.enabled ?? true;
  if (!enabled) {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          void registration.unregister();
        }
      });
    }
    if (typeof caches !== "undefined") {
      caches.keys().then((keys) => {
        for (const key of keys) {
          void caches.delete(key);
        }
      });
    }
    return;
  }
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  installControllerChangeReload();
  installServiceWorkerMessageBridge();
  if (registrationLoadListenerInstalled) return;
  registrationLoadListenerInstalled = true;
  window.addEventListener("load", () => {
    cleanupCmtsAppOwnedCaches()
      .then((result) => dispatchPwaEvent("cmts:pwa-cache-cleanup-completed", result))
      .catch((error: unknown) => reportServiceWorkerError(options, error, "cache-cleanup"))
      .finally(() => {
        navigator.serviceWorker.register(options.serviceWorkerUrl ?? "/cmts-service-worker.js", { updateViaCache: "none" })
          .then((registration) => {
            options.onReady?.(registration);
            postToWorker(registration.active, { type: "CMTS_CLEAR_STALE_CACHES" });
            if (registration.waiting) reportServiceWorkerUpdate(registration, options);
            registration.update().catch((error: unknown) => reportServiceWorkerError(options, error, "registration-update"));
            registration.addEventListener("updatefound", () => {
              const installing = registration.installing;
              if (!installing) return;
              installing.addEventListener("statechange", () => {
                if (installing.state === "installed" && navigator.serviceWorker.controller) {
                  reportServiceWorkerUpdate(registration, options);
                }
              });
            });
          })
          .catch((error: unknown) => reportServiceWorkerError(options, error, "register"));
      });
  });
}
